import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, Briefcase, Users, Star, Zap, ArrowRight } from 'lucide-react';
import SEO from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const CareersPage = () => {
  const { locale } = useParams();
  const [expandedRole, setExpandedRole] = useState(null);
  const [applyingFor, setApplyingFor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    coverNote: ''
  });

  const cultureValues = [
    { icon: Zap, title: 'Growth', description: 'Continuous learning and career development opportunities' },
    { icon: Star, title: 'Excellence', description: 'High standards in everything we deliver' },
    { icon: Users, title: 'Diversity', description: 'Inclusive environment that celebrates differences' },
    { icon: Briefcase, title: 'Impact', description: 'Work that shapes industries and transforms organizations' },
  ];

  const roles = {};

  const handleApply = (role) => {
    setApplyingFor(role);
    setFormData({ name: '', email: '', coverNote: '' });
  };

  const handleSubmitApplication = (e) => {
    e.preventDefault();
    // Mock submission
    alert(`Application submitted for ${applyingFor.title}!`);
    setApplyingFor(null);
  };

  return (
    <div className="pt-20" data-testid="careers-page">
      <SEO 
        title="Careers"
        description="Join H&G and shape the future of multi-disciplinary professional services. Explore career opportunities in pharma engineering, ESG, finance, capital markets, and M&A advisory."
        path="/careers"
      />
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-white via-light-grey to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy mb-6">
              Build What's Next, With Us
            </h1>
            <p className="text-lg text-mid-grey">
              Join a team of industry experts working on transformative projects for Fortune 500 organizations worldwide.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Culture */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              Our Culture
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">
              What Drives Us
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {cultureValues.map((value) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={value.title}
                  variants={fadeInUp}
                  className="text-center p-6 bg-light-grey rounded-xl"
                >
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                    <IconComponent className="w-7 h-7 text-teal" />
                  </div>
                  <h4 className="font-semibold text-navy mb-2">{value.title}</h4>
                  <p className="text-mid-grey text-sm">{value.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Open Roles */}
      <section className="py-16 md:py-20 bg-light-grey">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              Join Us
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">
              Open Positions
            </motion.h2>
          </motion.div>

          {Object.keys(roles).length === 0 ? (
            <div className="bg-white rounded-xl py-16 px-6 text-center">
              <Briefcase className="w-10 h-10 text-teal mx-auto mb-4" />
              <h3 className="font-playfair text-xl text-navy mb-2">No Open Positions Right Now</h3>
              <p className="text-mid-grey max-w-md mx-auto">
                We don't have any open roles at the moment, but we're always keen to hear from exceptional talent. Check back soon or reach out below.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(roles).map(([vertical, verticalRoles]) => (
                <motion.div
                  key={vertical}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedRole(expandedRole === vertical ? null : vertical)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-navy">{vertical}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-mid-grey">{verticalRoles.length} position{verticalRoles.length > 1 ? 's' : ''}</span>
                      <ChevronDown className={`w-5 h-5 text-mid-grey transition-transform ${expandedRole === vertical ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {expandedRole === vertical && (
                    <div className="border-t border-gray-100">
                      {verticalRoles.map((role) => (
                        <div key={role.id} className="px-6 py-4 border-b border-gray-50 last:border-b-0">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                              <h4 className="font-medium text-navy">{role.title}</h4>
                              <p className="text-sm text-mid-grey">{role.location} • {role.type}</p>
                            </div>
                            <button
                              onClick={() => handleApply(role)}
                              className="text-teal font-medium text-sm hover:text-teal transition-colors inline-flex items-center"
                            >
                              Apply Now <ArrowRight className="w-4 h-4 ml-1" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Application Modal */}
      {applyingFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full"
          >
            <h3 className="font-playfair text-2xl text-navy mb-2">Apply for Position</h3>
            <p className="text-mid-grey mb-6">{applyingFor.title}</p>
            
            <form onSubmit={handleSubmitApplication} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Full Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Resume</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="form-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Cover Note</label>
                <textarea
                  rows={3}
                  value={formData.coverNote}
                  onChange={(e) => setFormData({ ...formData, coverNote: e.target.value })}
                  className="form-input w-full resize-none"
                  placeholder="Tell us why you're interested..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setApplyingFor(null)}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-navy font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-teal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-playfair text-4xl text-white mb-6">
            Don't See the Right Role?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            We're always looking for exceptional talent. Send us your resume and we'll keep you in mind.
          </p>
          <Link to={`/${locale}/contact`} className="btn-primary">
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
